import { getSupabaseClient } from '@/core/supabase/client.js';
import type { AuditLogDto } from '../dtos/AuditLogDto.js';

const FIELDS = 'id, org_id, project_id, location_id, session_id, table_name, op, row_pk, actor_id, old_data, log_message, metadatas, changed_at';
interface AuditLogQueryResult {
  data: AuditLogDto[] | null;
  error: { message: string } | null;
  count: number | null;
}

interface AuditLogQuery extends PromiseLike<AuditLogQueryResult> {
  eq(column: string, value: string): AuditLogQuery;
  order(column: string, options: { ascending: boolean }): AuditLogQuery;
  ilike(column: string, pattern: string): AuditLogQuery;
  gte(column: string, value: string): AuditLogQuery;
  lte(column: string, value: string): AuditLogQuery;
  range(from: number, to: number): AuditLogQuery;
  limit(limit: number): AuditLogQuery;
}

interface AuditLogSupabaseClient {
  from(table: 'audit_log'): {
    select(fields: string, options: { count: 'estimated' }): AuditLogQuery;
  };
  rpc(
    functionName: 'get_project_latest_location_audit',
    params: { p_org_id: string; p_project_id: string },
  ): PromiseLike<{ data: AuditLogDto[] | null; error: { message: string } | null }>;
}


export interface AuditLogListParams {
  orgId: string;
  projectId?: string;
  locationId?: string;
  tableName?: string;
  op?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
  latestPerLocation?: boolean;
}

export class AuditLogRemoteDataSource {
  // The generated Supabase schema omits this table and RPC; constrain the local query surface.
  private get supabase() { return getSupabaseClient() as unknown as AuditLogSupabaseClient; }

  async list(params: AuditLogListParams): Promise<{
    data: AuditLogDto[];
    estimatedTotal: number;
    hasNext: boolean;
    nextPage?: { data: AuditLogDto[]; hasNext: boolean };
  }> {
    if (params.latestPerLocation && params.projectId) {
      const { data, error } = await this.supabase.rpc('get_project_latest_location_audit', {
        p_org_id: params.orgId,
        p_project_id: params.projectId,
      });
      if (error) throw new Error(error.message);
      return { data: data ?? [], estimatedTotal: data?.length ?? 0, hasNext: false };
    }

    let query = this.supabase.from('audit_log').select(FIELDS, { count: 'estimated' })
      .eq('org_id', params.orgId)
      .order('changed_at', { ascending: false });
    if (params.projectId) query = query.eq('project_id', params.projectId);
    if (params.locationId) query = query.eq('location_id', params.locationId);
    if (params.tableName) query = query.eq('table_name', params.tableName);
    if (params.op) query = query.eq('op', params.op);
    if (params.search) query = query.ilike('log_message', `%${params.search}%`);
    if (params.dateFrom) query = query.gte('changed_at', params.dateFrom);
    if (params.dateTo) query = query.lte('changed_at', params.dateTo);
    if (typeof params.limit === 'number' && typeof params.offset === 'number') {
      query = query.range(params.offset, params.offset + params.limit * 2);
    } else if (typeof params.limit === 'number') {
      query = query.limit(params.limit + 1);
    }
    const { data, error, count } = await query;
    if (error) throw new Error(error.message);
    const rows = data ?? [];
    const hasNext = typeof params.limit === 'number' && rows.length > params.limit;
    const nextPage = typeof params.limit === 'number' && typeof params.offset === 'number' && hasNext
      ? {
          data: rows.slice(params.limit, params.limit * 2),
          hasNext: rows.length > params.limit * 2,
        }
      : undefined;
    return {
      data: typeof params.limit === 'number' ? rows.slice(0, params.limit) : rows,
      estimatedTotal: count ?? 0,
      hasNext,
      ...(nextPage ? { nextPage } : {}),
    };
  }
}
